import { useMemo } from 'react';

import { getAllLocks } from 'api/locks/endpoints/getAllLocks';
import { formatLockPath, lockLabel, parseLockPath, type LockReference } from 'edit/reference/lockPath';

import { EditSubset } from './EditSubset';

// Module-level so the reference is stable: a fresh closure per render would rebuild the subset model
// and re-fetch on every render of the surrounding form.
const loadLocks = async (): Promise<readonly LockReference[]> => (await getAllLocks()).map((lock) => ({ zoneName: lock.zone_name, name: lock.name }));

interface Props {
  /** The stored `zone_name|name` lock paths. */
  readonly value: readonly string[];
  readonly onChange: (value: readonly string[]) => void;
  readonly label?: string;
  readonly disabled?: boolean;
}

/**
 * Picks the locks an object holds, from every lock the user may list.
 *
 * The selection is built by **splitting the stored paths**, never by looking them up among the
 * fetched rows: a path whose lock has been deleted, or which lives in a zone this user may not list,
 * still renders (flagged by the control) and survives the round trip rather than being dropped by
 * the next save. That also means the field reads correctly before the fetch resolves — the path
 * already carries both display parts.
 *
 * Stored as `zone_name|name`, shown as `name (zone)`. Those are trivially swappable, and swapping
 * them shows the user a raw config value or writes a display string into the config.
 */
export const EditLockSubset = ({ value, onChange, label = 'Locks', disabled }: Props) => {
  const selected = useMemo<readonly LockReference[]>(() => value.map(parseLockPath), [value]);

  return (
    <EditSubset<LockReference>
      label={label}
      value={selected}
      onChange={(locks) => onChange(locks.map(formatLockPath))}
      source={loadLocks}
      getKey={formatLockPath}
      getLabel={lockLabel}
      itemNoun="locks"
      disabled={disabled}
    />
  );
};
